import { useCallback, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock } from "lucide-react";

import { useAuth } from "@/features/auth/authContext";
import { useBot, useBotModules, useConnections, useUpsertModuleConfig } from "@/features/bots/hooks";
import { getApiErrorMessage } from "@/lib/api";
import { ModuleConfigForm } from "@/features/modules/ModuleConfigForm";
import { isModuleAllowed } from "@botwsp/shared";
import type { ModuleCode } from "@botwsp/shared";

export function BotDetailPage() {
  const { t } = useTranslation(["bots", "common", "errors"]);
  const { botId = "" } = useParams();
  const { tenantPlan } = useAuth();
  const { data: bot, isLoading: botLoading, isError: botError } = useBot(botId);
  const { data: modules, isLoading: modulesLoading } = useBotModules(botId);
  const { data: connections } = useConnections();
  const upsert = useUpsertModuleConfig(botId);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const connection = connections?.find((c) => c.bot_id === botId);

  const openConfig = useCallback(
    (code: string) => {
      setSelected(code);
      setFormError(null);
      dialogRef.current?.showModal();
    },
    [],
  );

  const closeConfig = useCallback(() => {
    dialogRef.current?.close();
    setSelected(null);
    setFormError(null);
  }, []);

  if (botLoading) return <p className="text-sm text-slate-500">{t("bots:detail.loadingBot")}</p>;
  if (botError || !bot) return <p className="text-sm text-red-600">{t("bots:detail.notFound")}</p>;

  const activeModule = modules?.find((m) => m.code === selected) ?? null;

  function handleSave(config: Record<string, unknown>) {
    if (!selected) return;
    setFormError(null);
    upsert.mutate(
      { moduleCode: selected, input: { enabled: true, configuration: config } },
      {
        onSuccess: () => {
          toast.success(t("bots:detail.configSaved"));
          closeConfig();
        },
        onError: (err) => setFormError(getApiErrorMessage(err)),
      },
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/bots" className="text-sm text-brand-700 hover:underline">
          {t("bots:detail.backToBots")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">{bot.name}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {bot.language.toUpperCase()} · {bot.timezone} ·{" "}
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              bot.status === "active"
                ? "bg-green-100 text-green-800"
                : bot.status === "suspended"
                  ? "bg-red-100 text-red-800"
                  : bot.status === "cancelled"
                    ? "bg-slate-100 text-slate-600"
                    : "bg-slate-100 text-slate-500"
            }`}
          >
            {t(`bots:detail.status.${bot.status}`, { defaultValue: bot.status })}
          </span>
        </p>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-base font-semibold">{t("bots:detail.connectionTitle")}</h2>
          <Link className="text-sm text-brand-700 hover:underline" to={`/bots/${bot.id}/onboarding`}>
            {t("common:actions.manage")}
          </Link>
        </div>
        <div className="card-body text-sm text-slate-500">
          {connection ? (
            <div className="space-y-1">
              <p>
                {t("common:states.status")}{" "}
                <span className="font-medium text-slate-800">{connection.connection_status}</span>
              </p>
              {connection.phone_number && (
                <p>
                  {t("bots:detail.numberLabel")} {connection.phone_number}
                </p>
              )}
              {connection.webhook_status && (
                <p>
                  {t("bots:detail.webhookLabel")} {connection.webhook_status}
                </p>
              )}
            </div>
          ) : (
            <p>{t("bots:detail.notConnected")}</p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-base font-semibold">{t("bots:detail.modulesTitle")}</h2>
        </div>
        {modulesLoading && <div className="card-body text-sm text-slate-500">{t("bots:detail.loadingModules")}</div>}
        {!modulesLoading && modules && (
          <div className="divide-y divide-slate-200">
            {modules.map((m) => {
              const allowed = tenantPlan ? isModuleAllowed(tenantPlan, m.code as ModuleCode) : false;
              return (
                <div key={m.code} className="flex items-center justify-between px-6 py-3 text-sm">
                  <div>
                    <p className="font-medium">{t(`bots:catalog.${m.code}.name`, { defaultValue: m.name })}</p>
                    <p className="text-xs text-slate-500">
                      {t(`bots:catalog.${m.code}.description`, { defaultValue: m.description })}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {allowed ? (
                      <>
                        <button
                          className={`text-sm ${m.enabled ? "text-brand-700" : "text-slate-400"} hover:underline`}
                          onClick={() => openConfig(m.code)}
                        >
                          {t("common:actions.configure")}
                        </button>
                        <label className="inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={m.enabled}
                            onChange={(e) =>
                              upsert.mutate({
                                moduleCode: m.code,
                                input: { enabled: e.target.checked, configuration: m.configuration },
                              })
                            }
                          />
                          <span className="h-6 w-11 rounded-full bg-slate-200 peer-checked:bg-brand-500 peer-focus:ring-2 peer-focus:ring-brand-500" />
                        </label>
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                        <Lock className="h-3.5 w-3.5" />
                        {t("bots:detail.planRequired")}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <dialog ref={dialogRef} className="modal" onClose={closeConfig}>
        {activeModule && (
          <div className="modal-box max-w-2xl">
            <h2 className="text-base font-semibold">
              {t("bots:detail.configTitle", {
                name: t(`bots:catalog.${activeModule.code}.name`, { defaultValue: activeModule.name }),
              })}
            </h2>
            <div className="mt-4">
              <ModuleConfigForm
                moduleCode={activeModule.code}
                initial={activeModule.configuration}
                onSave={handleSave}
                saving={upsert.isPending}
                error={formError}
              />
            </div>
            <div className="modal-action">
              <button className="btn-secondary" onClick={closeConfig}>
                {t("common:actions.close")}
              </button>
            </div>
          </div>
        )}
        <form method="dialog" className="modal-backdrop">
          <button>{t("common:actions.close")}</button>
        </form>
      </dialog>
    </div>
  );
}
